// src/components/ui/PageLoader.tsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

interface PageLoaderProps {
  isLoading: boolean;
  onComplete?: () => void;
}

const PageLoader: React.FC<PageLoaderProps> = ({ isLoading, onComplete }) => {
  const { theme } = useTheme();
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Loading');

  // Simulate loading progress
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => onComplete?.(), 300);
          return 100;
        }
        // Smooth progress increment
        const increment = Math.random() * 15 + 5;
        return Math.min(prev + increment, 100);
      });
    }, 150);

    return () => clearInterval(interval);
  }, [isLoading, onComplete]);

  // Animated loading text
  useEffect(() => {
    if (!isLoading) return;

    const textVariants = ['Loading', 'Loading.', 'Loading..', 'Loading...'];
    let index = 0;

    const textInterval = setInterval(() => {
      setLoadingText(textVariants[index]);
      index = (index + 1) % textVariants.length;
    }, 400);

    return () => clearInterval(textInterval);
  }, [isLoading]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] bg-gradient-to-br from-blue-50 via-white to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-400 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-emerald-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-purple-400 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>

          {/* Main Content */}
          <div className="relative z-10 text-center">
            {/* Logo Section */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mb-8"
            >
              <div className="relative">
                {/* Animated ring around logo */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 w-24 h-24 mx-auto"
                >
                  <div className="w-full h-full border-4 border-transparent border-t-blue-500 border-r-emerald-500 rounded-full" />
                </motion.div>
                
                {/* Actual KKN Logo */}
                <div className="relative w-24 h-24 mx-auto bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center shadow-xl border-2 border-gray-100 dark:border-slate-700">
                  <img 
                    src={theme === 'dark' ? "/assets/logo/logo_kkn_putih.png" : "/assets/logo/logo_kkn_hitam.png"}
                    alt="Logo KKN" 
                    className="w-20 h-20 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      if (target.parentElement) {
                        target.parentElement.innerHTML = `
                          <span class="text-blue-600 dark:text-blue-400 font-bold text-2xl">KKN</span>
                        `;
                      }
                    }}
                  />
                </div>
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                KKN DESA KARANGMALANG
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                UNIVERSITAS JENDERAL SOEDIRMAN
              </p>
            </motion.div>

            {/* Progress Section */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-8"
            >
              {/* Progress Bar */}
              <div className="w-64 sm:w-80 mx-auto mb-4">
                <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full relative"
                  >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 animate-shimmer" />
                  </motion.div>
                </div>
                
                {/* Progress percentage */}
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {loadingText}
                  </span>
                  <span className="text-sm font-mono text-blue-600 dark:text-blue-400">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Loading dots */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex justify-center space-x-2"
            >
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                  className="w-2 h-2 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
                />
              ))}
            </motion.div>

            {/* Fun loading message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="mt-6"
            >
              <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                Mempersiapkan pengalaman terbaik untuk Anda...
              </p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLoader;